import SectionCard from '../../components/SectionCard.jsx';
import { areaRows } from '../../lib/security-checks.js';
import { IDENTITY_NOTE, formatPoints, formatScoreDate, secureScoreCategories } from '../../lib/secure-score.js';

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * By area (Security, mockup 2): small multiples in two columns, one row anatomy (name, score, bar on the track, counts).
 * Left: the onGuard checks by area. Right: the Microsoft Secure Score by category, with the Identity disclaimer and the
 * points-only note kept from the old Microsoft Secure Score card, now full-width notes below both columns. It replaces the
 * 5 category KPI cards and that card.
 * Every value is visible text; the bars are aria-hidden decoration.
 */
export default function ByArea({ categories, msScore }) {
  const areas = areaRows(categories);
  const msRows = msScore ? secureScoreCategories(msScore) : [];
  return (
    <SectionCard title="By area" description="Where the strengths and gaps are" testId="security-by-area">
      <div className={CONTAINER} data-by-area-container="">
        <div className={COLUMNS} data-by-area-columns="">
          <div className={COLUMN} data-by-area-column="onguard">
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ipc-muted" id="by-area-onguard">onGuard checks</h3>
            {areas.length === 0 ? (
              <p className="text-sm text-ipc-muted">No area scores on the latest collection.</p>
            ) : (
              <ul aria-label="onGuard checks by area" className={ROWS}>
                {areas.map((a) => (
                  <SmallMultiple key={a.key} id={`area-${a.key}`} label={a.label} value={a.score} percent={a.score} caption={a.caption} />
                ))}
              </ul>
            )}
          </div>
          <MicrosoftColumn msScore={msScore} rows={msRows} />
        </div>
        <MicrosoftNotes msScore={msScore} rows={msRows} />
      </div>
    </SectionCard>
  );
}

function MicrosoftColumn({ msScore, rows }) {
  return (
    <div className={COLUMN} data-by-area-column="microsoft" data-testid="secure-score-by-category">
      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ipc-muted" id="by-area-microsoft">Microsoft Secure Score</h3>
      {!msScore ? (
        <p className="text-sm text-ipc-muted">Microsoft Secure Score was not collected on the latest collection.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ipc-muted">This snapshot has no score per category.</p>
      ) : (
        <ul aria-label="Microsoft Secure Score by category" className={ROWS}>
          {rows.map((r) => (
            <SmallMultiple
              key={r.key}
              id={`ms-${r.key}`}
              label={r.label}
              title={r.key === 'Identity' ? IDENTITY_NOTE : undefined}
              value={r.maxScore != null && finite(r.percentage) ? Math.round(r.percentage) : null}
              percent={r.maxScore != null ? r.percentage : null}
              caption={r.maxScore != null ? `${formatPoints(r.score)} of ${formatPoints(r.maxScore)} pts` : `${formatPoints(r.score)} pts`}
              sky
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The Microsoft Secure Score notes (product owner 2026-10-25): one full-width block below both columns, so the
 * explanation reads as a footnote of the whole card and never stretches the right column taller than the left.
 */
function MicrosoftNotes({ msScore, rows }) {
  if (!msScore) return null;
  const pointsOnly = rows.filter((r) => r.maxScore == null);
  const asOf = formatScoreDate(msScore.createdDateTime);
  return (
    <div className="mt-5 space-y-2 border-t border-ipc-border pt-3 text-[12.5px] text-ipc-muted" data-testid="by-area-notes">
      <p>
        {formatPoints(msScore.currentScore)} of {formatPoints(msScore.maxScore)} points, calculated by Microsoft across Microsoft 365 services{asOf ? `, as of ${asOf}` : ''}.
      </p>
      {rows.some((r) => r.key === 'Identity') ? <p data-testid="identity-category-note">{IDENTITY_NOTE}</p> : null}
      {pointsOnly.length > 0 ? (
        <p>
          {pointsOnly.length === rows.length
            ? 'This snapshot has no maximum per category, so categories show points earned only.'
            : `This snapshot has no maximum for ${pointsOnly.map((r) => r.label).join(', ')}, so ${pointsOnly.length === 1 ? 'it shows' : 'they show'} points earned only.`}
        </p>
      ) : null}
    </div>
  );
}

// Container-aware layout (ENTRAID-347, product owner 2026-10-25): the breakpoints read the card's own width, not the
// viewport, so a narrow card on a wide or vertical monitor never squeezes two full grids side by side. The card body
// is an inline-size container: the two columns sit side by side only from 55rem (two 26rem row grids plus the 3rem
// gap), otherwise onGuard stacks above Microsoft. Each column is a container too, so its rows use the aligned grid
// only from 26rem (the label, score, bar and widest detail fit) and otherwise stack, with the detail wrapping under
// the bar. Native CSS container queries through Tailwind arbitrary variants, no plugin; the class names stay literal
// so the Tailwind content scan finds them.
const CONTAINER = '[container-type:inline-size]';
const COLUMNS = 'grid grid-cols-1 gap-8 [@container(min-width:55rem)]:grid-cols-2 [@container(min-width:55rem)]:gap-12';
const COLUMN = 'min-w-0 [container-type:inline-size]';

// One grid for every row of a column (ENTRAID-347, product owner 2026-10-23): the rows are subgrids of the list, so the
// label, score, bar and detail columns line up from row to row however long a name is. Fixed label column (a long
// name wraps to at most two lines inside it), a right-aligned tabular score, the bar, then the detail sized to the
// widest detail. Every row has the same height, so the onGuard and Microsoft columns share one rhythm. In a column
// narrower than 26rem a row stacks: name and score, then the bar, then the detail, which may wrap.
const ROWS = '[@container(min-width:26rem)]:grid [@container(min-width:26rem)]:grid-cols-[minmax(7rem,12rem)_2.75rem_minmax(3rem,1fr)_max-content] [@container(min-width:26rem)]:gap-x-3.5';
const ROW = [
  'grid min-h-[3.25rem] grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3.5 gap-y-1 border-t border-ipc-border py-2 first:border-t-0',
  '[@container(min-width:26rem)]:col-span-4 [@container(min-width:26rem)]:grid-cols-subgrid [@container(min-width:26rem)]:gap-y-0',
].join(' ');
const BAR = 'col-span-2 block h-2 [@container(min-width:26rem)]:col-span-1';
const DETAIL = [
  'col-span-2 min-w-0 text-[12.5px] tabular-nums text-ipc-muted',
  '[@container(min-width:26rem)]:col-span-1 [@container(min-width:26rem)]:whitespace-nowrap [@container(min-width:26rem)]:text-right',
].join(' ');

/** One small-multiple row: name, the score in navy, a bar on the track and the counts, all visible text but the bar. */
function SmallMultiple({ id, label, title, value, percent, caption, sky = false }) {
  const width = finite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  return (
    <li className={ROW} data-small-multiple={id}>
      <span className="line-clamp-2 min-w-0 text-sm leading-5 text-ipc-ink" title={title}>{label}</span>
      <span className="text-right font-display text-xl font-extrabold leading-none tabular-nums text-ipc-primary" data-score="">
        {finite(value) ? value : '--'}
      </span>
      <span className={BAR} style={{ background: 'var(--track)' }} aria-hidden="true" data-bar="">
        {finite(percent) ? <i className="block h-full" style={{ width: `${width}%`, background: sky ? 'var(--sky)' : 'var(--blue)' }} /> : null}
      </span>
      <span className={DETAIL} data-detail="">{caption}</span>
    </li>
  );
}
